/**
 * Vector-pixel sprites: opaque cells stored as horizontal runs, drawn with
 * fillRect. Scales cleanly at any integer size — no soft bitmap resampling.
 */

export type PixelRun = {
  x: number;
  y: number;
  w: number;
  /** css #rrggbb */
  color: string;
};

export type VectorSprite = {
  w: number;
  h: number;
  runs: PixelRun[];
};

/** Lowest occupied row + 1 (content bottom). Falls back to sprite.h. */
export function vectorContentBottom(sprite: VectorSprite): number {
  let maxY = -1;
  for (const run of sprite.runs) {
    if (run.y > maxY) maxY = run.y;
  }
  return maxY < 0 ? sprite.h : maxY + 1;
}

function hex(r: number, g: number, b: number): string {
  return (
    "#" +
    ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)
  );
}

/**
 * Convert a baked canvas into hard-edged vector runs.
 * Semi-transparent / fringe pixels are dropped (alpha gate).
 */
export function canvasToVectorSprite(
  canvas: HTMLCanvasElement,
  alphaGate = 140,
): VectorSprite {
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext("2d")!;
  const { data } = ctx.getImageData(0, 0, w, h);
  const runs: PixelRun[] = [];

  for (let y = 0; y < h; y++) {
    let runX = -1;
    let runColor = "";
    let runW = 0;
    const flush = () => {
      if (runW > 0) {
        runs.push({ x: runX, y, w: runW, color: runColor });
      }
      runX = -1;
      runW = 0;
      runColor = "";
    };
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const a = data[i + 3]!;
      if (a < alphaGate) {
        flush();
        continue;
      }
      const color = hex(data[i]!, data[i + 1]!, data[i + 2]!);
      if (runW > 0 && color === runColor && x === runX + runW) {
        runW++;
      } else {
        flush();
        runX = x;
        runColor = color;
        runW = 1;
      }
    }
    flush();
  }

  return { w, h, runs };
}

/** Draw vector-pixel art with fillRect — integer position, optional flipX. */
export function drawVectorSprite(
  ctx: CanvasRenderingContext2D,
  sprite: VectorSprite,
  x: number,
  y: number,
  opts?: { flipX?: boolean; scale?: number },
): void {
  const scale = Math.max(1, Math.round(opts?.scale ?? 1));
  const flipX = opts?.flipX ?? false;
  const ox = Math.round(x);
  const oy = Math.round(y);
  ctx.imageSmoothingEnabled = false;

  let prev = "";
  for (const run of sprite.runs) {
    if (run.color !== prev) {
      ctx.fillStyle = run.color;
      prev = run.color;
    }
    const rx = flipX ? sprite.w - run.x - run.w : run.x;
    ctx.fillRect(ox + rx * scale, oy + run.y * scale, run.w * scale, scale);
  }
}

/** Optional SVG serialization (sharp rects) — useful for export / debugging. */
export function vectorSpriteToSvg(sprite: VectorSprite): string {
  const parts = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${sprite.w}" height="${sprite.h}" shape-rendering="crispEdges" viewBox="0 0 ${sprite.w} ${sprite.h}">`,
  ];
  for (const run of sprite.runs) {
    parts.push(
      `<rect x="${run.x}" y="${run.y}" width="${run.w}" height="1" fill="${run.color}"/>`,
    );
  }
  parts.push("</svg>");
  return parts.join("");
}

/** Rebuild a canvas from vector runs (hard pixels, no soft edges). */
export function vectorSpriteToCanvas(sprite: VectorSprite): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = sprite.w;
  c.height = sprite.h;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  drawVectorSprite(ctx, sprite, 0, 0);
  return c;
}
